import React, { useState, useEffect, useRef } from "react";
import { X, CloudUpload, Download, Plus, UploadCloud } from "lucide-react";
import { createAdminProduct } from "../services/productsApi";
import { useAuth } from "../auth/AuthContext";
import { Product } from "../types/product";

interface AddProductModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProductCreated: (product: Product) => void;
  initialBarcode?: string;
}

const toNumber = (...values: (string | number | undefined | null)[]): number | null => {
  for (const val of values) {
    if (val === null || val === undefined) continue;
    const num = typeof val === "string" ? parseFloat(val) : Number(val);
    if (!Number.isNaN(num)) return num;
  }
  return null;
};

export const AddProductModal: React.FC<AddProductModalProps> = ({
  isOpen,
  onClose,
  onProductCreated,
  initialBarcode = "",
}) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: "",
    price: "",
    barcode: initialBarcode,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bulk Upload State
  const [modalMode, setModalMode] = useState<"single" | "bulk">("single");
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [isCsvDragOver, setIsCsvDragOver] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: "",
        price: "",
        barcode: initialBarcode,
      });
      setError(null);
    }
  }, [isOpen, initialBarcode]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError("Name is required");
      return;
    }
    if (!formData.price.trim()) {
      setError("Price is required");
      return;
    }
    const priceNum = parseFloat(formData.price);
    if (Number.isNaN(priceNum) || priceNum <= 0) {
      setError("Price must be a valid positive number");
      return;
    }
    if (!formData.barcode.trim()) {
      setError("Barcode is required");
      return;
    }

    setLoading(true);

    try {
      const companyId =
        toNumber(user?.company_id, user?.company?.id, user?.companyId) ?? 1;
      const locationId =
        toNumber(
          user?.location_id,
          user?.location?.id,
          user?.locationId,
          user?.default_location_id
        ) ?? 1;

      const product = await createAdminProduct({
        company_id: companyId,
        location_id: locationId,
        name: formData.name.trim(),
        price: priceNum,
        barcode: formData.barcode.trim(),
      });

      onProductCreated(product);
      onClose();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to create product. Please try again."
      );
      console.error("Error creating product:", err);
    } finally {
      setLoading(false);
    }
  };

  // CSV Drag and Drop Handlers
  const handleCsvDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCsvDragOver(true);
  };

  const handleCsvDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCsvDragOver(false);
  };

  const handleCsvDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsCsvDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0 && files[0].name.endsWith(".csv")) {
      setCsvFile(files[0]);
    } else {
      setError("Please upload a valid CSV file");
    }
  };

  const handleCsvFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (files[0].name.endsWith(".csv")) {
        setCsvFile(files[0]);
      } else {
        setError("Please upload a valid CSV file");
      }
    }
  };

  const handleBulkSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!csvFile) {
      setError("Please select a CSV file to upload");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const api = (await import("../services/api")).default;
      const formData = new FormData();
      formData.append("file", csvFile);

      const response = await api.post("/admin/products/bulk-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      // Show success and close
      onClose();
      // Since it's POS, we might need a full refresh or a specific product list refresh
      // For now, reload as in ServiceModal for consistency
      window.location.reload();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        "Failed to upload products. Please check your CSV file and try again."
      );
      console.error("Bulk upload error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSample = () => {
    window.open("https://salonify.co.uk/csv-samples/products.csv", "_blank");
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px] z-[1100]">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-md flex flex-col max-h-[90vh] relative">
        {/* Header */}
        <div className="px-6 pt-6 pb-2 border-b flex flex-col gap-4">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {modalMode === "single" ? "Add New Product" : "Bulk Upload Products"}
            </h2>
            <div className="flex items-center gap-2">
              {modalMode === "bulk" && (
                <button
                  type="button"
                  onClick={handleDownloadSample}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-[#885ABB] bg-white rounded-lg hover:bg-purple-50 transition-colors border border-[#885ABB]"
                >
                  <Download className="w-4 h-4" />
                  <span>Sample</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-[#885ABB] hover:bg-purple-50 rounded-lg transition-all"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => setModalMode("single")}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${modalMode === "single"
                ? "bg-white text-[#885ABB] shadow-sm"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              <Plus className="w-4 h-4" />
              Add Product
            </button>
            <button
              type="button"
              onClick={() => setModalMode("bulk")}
              className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${modalMode === "bulk"
                ? "bg-white text-[#885ABB] shadow-sm"
                : "text-gray-500 hover:text-gray-700"
                }`}
            >
              <CloudUpload className="w-4 h-4" />
              Bulk Upload
            </button>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
          {modalMode === "single" ? (
            <form id="add-product-form" onSubmit={handleSubmit} className="space-y-4">
              {/* Name Field */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                  Product Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full h-12 border border-gray-100 rounded-xl px-4 text-[#402B69] font-semibold bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all"
                  placeholder="e.g. Shampoo"
                  disabled={loading}
                />
              </div>

              {/* Price Field */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                  Retail Price
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 font-bold">£</span>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    className="w-full h-12 border border-gray-100 rounded-xl pl-10 pr-4 text-[#402B69] font-bold bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all"
                    placeholder="0.00"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Barcode Field */}
              <div>
                <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-2 px-1">
                  Barcode / SKU
                </label>
                <input
                  type="text"
                  name="barcode"
                  value={formData.barcode}
                  onChange={handleChange}
                  className="w-full h-12 border border-gray-100 rounded-xl px-4 text-[#402B69] font-semibold bg-gray-50/50 focus:outline-none focus:ring-2 focus:ring-[#885ABB]/20 focus:border-[#885ABB] transition-all"
                  placeholder="Scan or enter barcode"
                  disabled={loading}
                />
              </div>

              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-100 text-red-600 text-xs font-bold px-4 py-3 rounded-xl">
                  {error}
                </div>
              )}
            </form>
          ) : (
            <div className="py-6">
              <div
                onDragOver={handleCsvDragOver}
                onDragLeave={handleCsvDragLeave}
                onDrop={handleCsvDrop}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-all ${isCsvDragOver
                  ? "border-[#885ABB] bg-purple-50"
                  : "border-gray-200 bg-gray-50"
                  }`}
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-purple-100">
                  <CloudUpload className="h-7 w-7 text-[#885ABB]" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  Upload Products CSV
                </h3>
                <p className="text-sm text-gray-500 mb-6 max-w-xs mx-auto">
                  Drag and drop your products CSV file here, or click to browse.
                </p>
                <input
                  type="file"
                  ref={csvInputRef}
                  onChange={handleCsvFileSelect}
                  accept=".csv"
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => csvInputRef.current?.click()}
                  className="px-6 py-2 bg-white border border-gray-200 text-gray-700 font-bold rounded-xl hover:bg-gray-50 transition-all shadow-sm"
                >
                  Browse Files
                </button>

                {csvFile && (
                  <div className="mt-6 p-4 bg-white rounded-xl border border-purple-100 flex items-center justify-between max-w-sm mx-auto">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-50 rounded-lg">
                        <UploadCloud className="w-5 h-5 text-green-600" />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-bold text-gray-900 truncate max-w-[150px]">
                          {csvFile.name}
                        </p>
                        <p className="text-xs text-gray-500">
                          {(csvFile.size / 1024).toFixed(2)} KB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setCsvFile(null)}
                      className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
                {/* Error Message for Bulk */}
                {error && (
                  <div className="mt-4 bg-red-50 border border-red-100 text-red-600 text-xs font-bold px-4 py-3 rounded-xl">
                    {error}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 h-12 bg-gray-50 text-gray-600 rounded-xl font-bold hover:bg-gray-100 transition-all border border-gray-100"
          >
            Cancel
          </button>
          <button
            form={modalMode === "single" ? "add-product-form" : undefined}
            onClick={modalMode === "bulk" ? handleBulkSubmit : undefined}
            type="submit"
            disabled={loading || (modalMode === "single" ? !formData.name : !csvFile)}
            className="flex-[2] h-12 bg-[#885ABB] hover:bg-[#7748A4] text-white rounded-xl font-bold transition-all shadow-lg shadow-purple-100 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                <span>{modalMode === "single" ? "Creating..." : "Uploading..."}</span>
              </>
            ) : (
              <span>{modalMode === "single" ? "Create Product" : "Upload Products"}</span>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
